import { useMemo } from 'react'
import { useDebts } from './useDebts'
import { useUIStore } from '@/store'
import { pathToFreedom, totalMonthsToFreedom, type Milestone } from '@/engine/pathToFreedom'

/**
 * Facade for the Path to Freedom module.
 *
 * Derives the chronological milestone list from the current registry,
 * the selected strategy in `uiStore`, and the extra-payment lever shared
 * with the Strategy Engine. Recomputes on every change to any of those —
 * the engine is fast (~50ms for a 30-debt portfolio at 360 months) and
 * the output is memoised so re-renders elsewhere don't re-trigger.
 *
 * Returns the milestone list plus a few derived rollups the UI needs:
 *   - `totalMonths` — debt-free in N months (null if any debt never converges)
 *   - `convergingCount` — how many of the milestones actually have a date
 *   - `neverConvergesCount` — how many won't clear under the current plan
 */
export function usePathToFreedom() {
  const { debts } = useDebts()
  const selectedStrategy = useUIStore((s) => s.selectedStrategy)
  const extraMonthlyPayment = useUIStore((s) => s.extraMonthlyPayment)

  const milestones = useMemo<Milestone[]>(
    () => pathToFreedom(debts, selectedStrategy, extraMonthlyPayment),
    [debts, selectedStrategy, extraMonthlyPayment]
  )

  const convergingCount = milestones.filter((m) => m.state === 'projected').length
  const neverConvergesCount = milestones.length - convergingCount
  const totalMonths = useMemo(() => totalMonthsToFreedom(milestones), [milestones])

  return {
    milestones,
    selectedStrategy,
    extraMonthlyPayment,
    totalMonths,
    convergingCount,
    neverConvergesCount,
  }
}
